import { useRef, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import axios from "../../api/axiosClient";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  Stack,
} from "@mui/material";
import PageHeader from "../ui/PageHeader";
import CustomInput from "../ui/CustomInput";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { DataGrid } from "@mui/x-data-grid";

import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { handleError } from "../../services/serviceBase";
import { useLoading } from "../LoadingContext";

const BasketCreationModal = (props) => {
  const intl = useIntl();
  const idCompany = useSelector((state) => state.common.company);
  const { setLoading } = useLoading();

  const [destination, setDestination] = useState("");
  const [destinations, setDestinations] = useState([]);
  const [sender, setSender] = useState({ text: "", value: null });
  const [senders, setSenders] = useState(props.senders);
  const [isTriangulation, setIsTriangulation] = useState(false);
  const [isDamaged, setIsDamaged] = useState(false);
  const [salePrice, setSalePrice] = useState("MarkDown");

  const [basketDetails, setBasketDetails] = useState([
    { id: 1, sku: "", quantity: "", boxNr: "" },
  ]);

  const nextId = useRef(2);

  const handleReset = () => {
    setBasketDetails([{ id: 1, sku: "", quantity: "", boxNr: "" }]);
    setDestination("");
    setDestinations([]);
    setIsTriangulation(false);
    setIsDamaged(false);
    setSender({ text: "", value: null });
    setSalePrice("MarkDown");
  };

  useEffect(() => {
    handleReset();
    if (props.senders.length > 0) {
      setSenders(props.senders);
    }
  }, [props.openModal]);

  useEffect(() => {
    if (sender.value) {
      getDestinations();
    } else {
      setDestinations([]);
    }
  }, [sender, salePrice, isTriangulation, isDamaged]);

  const columns = [
    {
      field: "sku",
      headerName: "SKU",
      flex: 1,
      editable: true,
    },
    {
      field: "quantity",
      headerName: intl.formatMessage({ id: "BasketCreation.Quantity" }),
      flex: 1,
      editable: true,
      type: "number",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "boxNr",
      headerName: "Box Nr",
      flex: 1,
      editable: true,
      type: "number",
      headerAlign: "left",
      align: "left",
    },
  ];

  const addRow = () => {
    setBasketDetails((prev) => [
      ...prev,
      { id: nextId.current++, sku: "", quantity: "", boxNr: "" },
    ]);
  };

  const processRowUpdate = (newRow) => {
    setBasketDetails((prev) =>
      prev.map((r) => (r.id === newRow.id ? newRow : r)),
    );
    return newRow;
  };

  const isRowPopulated = (row) => {
    return ["sku", "quantity", "boxNr"].some((k) => {
      const v = row[k];
      if (typeof v === "number") return !Number.isNaN(v);
      return String(v ?? "").trim() !== "";
    });
  };

  const removeLastRow = () => {
    if (basketDetails.length === 0) return;
    const last = basketDetails[basketDetails.length - 1];
    const needsConfirm = isRowPopulated(last);
    if (
      !needsConfirm ||
      window.confirm(
        intl.formatMessage({ id: "BasketCreation.ConfirmRowDelete" }),
      )
    ) {
      setBasketDetails((prev) => prev.slice(0, -1));
    }
  };

  const modalStyle = {
    position: "relative",
    top: "50%",
    left: "50%",
    width: "70%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    p: 4,
    borderRadius: 2,
  };

  const handleSalePriceChange = (e) => {
    setSalePrice(e.currentTarget.value);
  };

  const getDestinations = async () => {
    setLoading(true);
    var url = "/s2s/baskets/destinations";
    const params = new URLSearchParams([]);
    params.append("idCompany", idCompany);
    params.append("senderJDALocation", sender.value);
    params.append("salePriceType", salePrice);
    params.append("isTriang", isTriangulation);
    params.append("isDamaged", isDamaged);

    axios
      .get(url, { params })
      .then((response) => {
        setLoading(false);
        var items = [];
        if (response.data !== undefined) {
          response.data.map((x) => {
            items.push({
              value: x.jdaLocation,
              text: `${x.jdaLocation} - ${x.description}`,
            });
          });
        }
        setDestinations(items);
        if (items.length == 1) {
          setDestination(items[0].value);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error?.response?.data?.type == "GenericError") {
          toast.error(
            intl.formatMessage({ id: "Error.ErrorInBasketCreation" }),
          );
        } else {
          handleError(error, intl);
        }
      });
  };

  const handleCreateBasket = () => {
    setLoading(true);
    let bodyItems = [];
    basketDetails.forEach((element) => {
      if (element.sku.length != 9) {
        toast.error(
          intl.formatMessage({ id: "BasketCreation.ArticleBarcodeLength" }),
        );
        return;
      }

      if (
        element.boxNr.length != 0 &&
        element.sku.length != 0 &&
        element.quantity.length != 0
      ) {
        bodyItems.push({
          BoxNumber: element.boxNr,
          Sku: element.sku,
          Quantity: element.quantity,
        });
      }
    });

    if (
      (sender.value != undefined && sender.value.length == 0) ||
      destination.length == 0 ||
      bodyItems.length == 0
    ) {
      setLoading(false);
      toast.error(intl.formatMessage({ id: "BasketCreation.MandatoryFields" }));
      return;
    }

    let request = {
      IdCompany: idCompany,
      JDALocationSender: sender.value,
      JDALocationDestination: destination,
      SalePriceType: salePrice,
      BodyItems: bodyItems,
    };

    var url = "/s2s/baskets";
    axios
      .post(url, request)
      .then((response) => {
        setLoading(false);
        toast.success("Basket created!");
        props.handleCloseModal(response.data);
      })
      .catch((error) => {
        setLoading(false);
        if (error?.response?.data?.type == "NoJDAStockFound") {
          toast.error(
            intl.formatMessage({ id: "Error.NoJDAStockFoundBasket" }),
          );
        } else {
          handleError(error, intl);
        }
      });
  };

  return (
    <Modal open={props.openModal} onClose={() => props.handleCloseModal()}>
      <Box sx={modalStyle}>
        <PageHeader
          title={intl.formatMessage({ id: "BasketCreation.Title" })}
        />
        <form id="form">
          <Grid container mt={2}>
            <Grid item md={4}>
              <CustomInput
                color="blue"
                labelText={intl.formatMessage({ id: "BasketCreation.Sender" })}
                type="autocomplete"
                textCol={3}
                inputCol={8}
                valueRef={sender}
                selectItems={senders}
                setValue={setSender}
              />
            </Grid>
            <Grid item md={2}>
              <FormControl>
                <RadioGroup
                  defaultValue="MarkDown"
                  name="radio-buttons-group"
                  sx={{
                    border: 2,
                    borderColor: "#85ABD5",
                    borderRadius: "15px",
                    paddingRight: "15px",
                  }}
                  onChange={handleSalePriceChange}
                >
                  <FormControlLabel
                    value="MarkDown"
                    control={<Radio />}
                    label={
                      <Typography sx={{ fontSize: 12 }}>
                        {intl.formatMessage({ id: "General.MarkDown" })}
                      </Typography>
                    }
                    labelPlacement="start"
                  />
                  <FormControlLabel
                    value="FullPrice"
                    control={<Radio />}
                    label={
                      <Typography sx={{ fontSize: 12 }}>
                        {intl.formatMessage({ id: "General.FullPrice" })}
                      </Typography>
                    }
                    labelPlacement="start"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item md={2}>
              <FormControl>
                <FormGroup
                  sx={{
                    border: 2,
                    borderColor: "#85ABD5",
                    borderRadius: "15px",
                    paddingRight: "15px",
                  }}
                >
                  <FormControlLabel
                    control={<Checkbox />}
                    label={
                      <Typography sx={{ fontSize: 12 }}>
                        {intl.formatMessage({
                          id: "BasketCreation.IsTriangulation",
                        })}
                      </Typography>
                    }
                    labelPlacement="start"
                    onChange={(e) => {
                      setIsTriangulation(e.target.checked);
                    }}
                  />
                  <FormControlLabel
                    control={<Checkbox />}
                    label={
                      <Typography sx={{ fontSize: 12 }}>
                        {intl.formatMessage({ id: "General.Damaged" })}
                      </Typography>
                    }
                    labelPlacement="start"
                    onChange={(e) => {
                      setIsDamaged(e.target.checked);
                    }}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item md={4}>
              <CustomInput
                color="blue"
                labelText={intl.formatMessage({
                  id: "BasketCreation.Destination",
                })}
                type="select"
                textCol={4}
                inputCol={8}
                valueRef={destination}
                selectItems={destinations}
                setValue={setDestination}
                //selectFirst={true}
              />
            </Grid>
          </Grid>

          <Grid container sx={{ mt: 2 }}>
            <Grid item md={12}>
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={addRow}
                  >
                    {intl.formatMessage({ id: "General.Add" })}
                  </Button>
                  <Button
                    startIcon={<RemoveIcon />}
                    variant="outlined"
                    color="warning"
                    onClick={removeLastRow}
                    disabled={basketDetails.length === 0}
                  >
                    {intl.formatMessage({ id: "General.Remove" })}
                  </Button>
                </Stack>

                <div style={{ height: 300, width: "100%" }}>
                  <DataGrid
                    rows={basketDetails}
                    columns={columns}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(e) => console.error(e)}
                    hideFooter
                  />
                </div>
              </Box>
            </Grid>
            <Grid item md={10}></Grid>
            <Grid item md={2} display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                className="apeLigthBlueBackground"
                onClick={handleCreateBasket}
              >
                {intl.formatMessage({ id: "BasketCreation.Create" })}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

export default BasketCreationModal;
